/**
 * CP-118: Coherent card illustration system (vector motifs + destination gradients).
 * No photos / licensing — intentional SVG + CSS only.
 *
 * Cards resolve a theme from destination_id, then destination label / itinerary
 * keywords, else a brand-teal default ship motif. Same cruise attributes always
 * map to the same art (deterministic).
 */

const THEMES = {
  caribbean: {
    id: 'caribbean',
    gradient: 'linear-gradient(135deg, #0B5B63 0%, #1697A6 55%, #0E7A84 100%)',
    motif: 'caribbean',
    label: 'Caribbean',
  },
  bahamas: {
    id: 'bahamas',
    gradient: 'linear-gradient(135deg, #008B8B 0%, #48D1CC 55%, #1A9E9E 100%)',
    motif: 'bahamas',
    label: 'Bahamas',
  },
  alaska: {
    id: 'alaska',
    gradient: 'linear-gradient(135deg, #1A3D54 0%, #4A7C9B 55%, #2A5570 100%)',
    motif: 'alaska',
    label: 'Alaska',
  },
  europe: {
    id: 'europe',
    gradient: 'linear-gradient(135deg, #9E472A 0%, #3A6B88 55%, #5A4A6A 100%)',
    motif: 'europe',
    label: 'Europe & Mediterranean',
  },
  mexico: {
    id: 'mexico',
    gradient: 'linear-gradient(135deg, #C45C26 0%, #E28743 55%, #A64B1E 100%)',
    motif: 'mexico',
    label: 'Mexican Riviera',
  },
  bermuda: {
    id: 'bermuda',
    gradient: 'linear-gradient(135deg, #083F45 0%, #1B6B76 55%, #0B5B63 100%)',
    motif: 'bermuda',
    label: 'Bermuda & Atlantic',
  },
  pacific: {
    id: 'pacific',
    gradient: 'linear-gradient(135deg, #0B5B63 0%, #2A6F74 45%, #5B8A8E 100%)',
    motif: 'pacific',
    label: 'Pacific Coast',
  },
  hawaii: {
    id: 'hawaii',
    gradient: 'linear-gradient(135deg, #0E6B6E 0%, #1B8A8E 40%, #D4A017 100%)',
    motif: 'hawaii',
    label: 'Hawaii',
  },
  australia: {
    id: 'australia',
    gradient: 'linear-gradient(135deg, #1B4F72 0%, #2874A6 50%, #1A7A6E 100%)',
    motif: 'australia',
    label: 'Australia & South Pacific',
  },
  default: {
    id: 'default',
    gradient: 'linear-gradient(135deg, #0B5B63 0%, #083F45 55%, #1B6B76 100%)',
    motif: 'default',
    label: 'Cruise',
  },
};

/** Canonical destination_id → theme id (aligned with DestinationGrid / filters). */
const ID_TO_THEME = {
  caribbean: 'caribbean',
  western_caribbean: 'caribbean',
  eastern_caribbean: 'caribbean',
  southern_caribbean: 'caribbean',
  bahamas: 'bahamas',
  alaska: 'alaska',
  europe: 'europe',
  mediterranean: 'europe',
  mexico: 'mexico',
  mexican_riviera: 'mexico',
  bermuda: 'bermuda',
  atlantic: 'bermuda',
  pacific_coast: 'pacific',
  pacific: 'pacific',
  hawaii: 'hawaii',
  australia_nz: 'australia',
  australia: 'australia',
  south_pacific: 'australia',
  canada_new_england: 'bermuda',
  panama_canal: 'caribbean',
  transatlantic: 'bermuda',
  asia: 'default',
};

/**
 * Ordered keyword rules applied to `${destination} ${name}` when id is unknown.
 * More specific regions first.
 */
const KEYWORD_RULES = [
  { theme: 'alaska', re: /\balaska\b|\bglacier\b|inside\s*passage|\bjuneau\b|\bketchikan\b|\bskagway\b/i },
  { theme: 'hawaii', re: /\bhawaii\b|\bhawaiian\b|\bmaui\b|\boahu\b|\bkauai\b|\bhonolulu\b/i },
  { theme: 'bahamas', re: /\bbahamas\b|\bnassau\b|\bfreeport\b|\bcoco\s*cay\b/i },
  { theme: 'mexico', re: /\bmexico\b|\bmexican\b|\briviera\b|\bcabo\b|\bmazatlan\b|\bpuerto\s*vallarta\b|\bensenada\b|\bbaja\b/i },
  { theme: 'caribbean', re: /\bcaribbean\b|\bcozumel\b|\bjamaica\b|\bbarbados\b|\bst\.?\s*thomas\b|\baruba\b|\bcuracao\b|\bprivate\s*cay\b/i },
  { theme: 'europe', re: /\bmediterranean\b|\beurope\b|\bgreek\b|\bbaltic\b|\bnorway\b|\bfjord\b|\bbarcelona\b|\brome\b|\bvenice\b|\bcivita\b|\bsantorini\b|\bathens\b|\blisbon\b|\bsouthampton\b/i },
  { theme: 'bermuda', re: /\bbermuda\b|\btransatlantic\b|\bcanada\b|\bnew\s*england\b|\bhalifax\b|\bboston\b|\batlantic\b/i },
  { theme: 'australia', re: /\baustralia\b|\bnew\s*zealand\b|\bsydney\b|\bauckland\b|\bsouth\s*pacific\b|\bfiji\b|\btahiti\b/i },
  { theme: 'pacific', re: /\bpacific\s*coast\b|\bpacific\s*coastal\b|\bcalifornia\b|\bloses?\s*angeles\b|\bsan\s*diego\b|\bsan\s*francisco\b|\bseattle\b|\bvancouver\b|\bvictoria\b/i },
];

function normalizeId(id) {
  if (id == null || id === '') return '';
  return String(id).trim().toLowerCase().replace(/[\s-]+/g, '_');
}

function themeById(id) {
  const key = ID_TO_THEME[normalizeId(id)];
  return key ? THEMES[key] : null;
}

function themeByKeywords(text) {
  if (!text) return null;
  for (const rule of KEYWORD_RULES) {
    if (rule.re.test(text)) return THEMES[rule.theme];
  }
  return null;
}

/**
 * Resolve illustration theme for a cruise/deal card.
 * @param {{ destination?: string, destination_id?: string, name?: string, itinerary?: string }} cruise
 * @returns {{ id: string, gradient: string, motif: string, motifSrc: string, label: string }}
 */
export function resolveCardIllustration(cruise = {}) {
  const destinationId = cruise.destination_id || cruise.destinationId || '';
  const destination = cruise.destination || '';
  const name = cruise.name || cruise.itinerary || '';

  const fromId = themeById(destinationId);
  const fromKeywords = themeByKeywords(`${destination} ${name}`);
  const theme = fromId || fromKeywords || THEMES.default;

  return {
    id: theme.id,
    gradient: theme.gradient,
    motif: theme.motif,
    motifSrc: `/illustrations/card-${theme.motif}.svg`,
    label: theme.label,
  };
}

/** Exported for tests / DestinationGrid alignment checks. */
export function listCardIllustrationThemes() {
  return Object.keys(THEMES);
}

export { THEMES, ID_TO_THEME, KEYWORD_RULES };
